"use client";

import React, { useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { IconHome, IconBriefcase, IconUser, IconCarambola, IconCar } from "@tabler/icons-react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";

type DashboardTabValue = "home" | "habilidades" | "projetos" | "perfil";

interface DashboardTabsProps {
  value: DashboardTabValue;
  onChange: (value: DashboardTabValue) => void;
  children?: {
    home?: React.ReactNode;
    habilidades?: React.ReactNode;
    projetos?: React.ReactNode;
    perfil?: React.ReactNode;
  };
}

export default function DashboardTabs({ value, onChange, children }: DashboardTabsProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // 🔁 Quando a URL muda (?tab=...), sincroniza com o state externo
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab") as DashboardTabValue | null;

    if (tabFromUrl && tabFromUrl !== value) {
      onChange(tabFromUrl);
    }
  }, [searchParams, value, onChange]);

  const handleChange = (tab: DashboardTabValue) => {
    onChange(tab);

    const params = new URLSearchParams(searchParams.toString());
    params.set("tab", tab);

    // Como sua rota é "/", garantimos isso aqui
    router.push(`${pathname}?${params.toString()}`);
  };

  const triggerBase = [
    "flex items-center justify-center gap-2",
    "px-2 py-2 md:px-4 md:py-2",
    "text-xs sm:text-sm cursor-pointer",
    "rounded-full whitespace-nowrap",
    "transition-all duration-150",
    "data-[state=inactive]:text-neutral-400",
    "data-[state=active]:text-black",
    "hover:bg-black/5",
    "data-[state=active]:bg-black/10",
  ].join(" ");

  return (
    <Tabs value={value} onValueChange={v => handleChange(v as DashboardTabValue)} className="w-full">
      {/* 🔹 DESKTOP */}
      <div className="hidden md:block w-full">
        <TabsList className="mb-6 flex flex-row rounded-full p-1 gap-1 w-full bg-white border border-default-200">
          <TabsTrigger value="home" className={triggerBase}>
            <IconHome size={16} />
            <span>Home</span>
          </TabsTrigger>

          <TabsTrigger value="habilidades" className={triggerBase}>
            <IconCarambola size={16} />
            <span>Habilidades</span>
          </TabsTrigger>

          <TabsTrigger value="projetos" className={triggerBase}>
            <IconBriefcase size={16} />
            <span>Projetos</span>
          </TabsTrigger>

          <TabsTrigger value="perfil" className={triggerBase}>
            <IconUser size={16} />
            <span>Perfil</span>
          </TabsTrigger>
        </TabsList>
      </div>

      {/* 🔹 MOBILE */}
      <div className="md:hidden mb-3">
        <TabsList className="w-full p-2 rounded-2xl bg-white border border-default-200">
          <TabsTrigger value="home" className={triggerBase + " w-full"}>
            <IconHome size={16} />
            Home
          </TabsTrigger>

          <TabsTrigger value="habilidades" className={triggerBase + " w-full"}>
            <IconCarambola size={16} />
            Habilidades
          </TabsTrigger>

          <TabsTrigger value="projetos" className={triggerBase + " w-full"}>
            <IconBriefcase size={16} />
            Projetos
          </TabsTrigger>

          <TabsTrigger value="perfil" className={triggerBase + " w-full"}>
            <IconUser size={16} />
            Perfil
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="home" className="mt-2">
        {children?.home}
      </TabsContent>
      <TabsContent value="habilidades" className="mt-2">
        {children?.habilidades}
      </TabsContent>
      <TabsContent value="projetos" className="mt-2">
        {children?.projetos}
      </TabsContent>
      <TabsContent value="perfil" className="mt-2">
        {children?.perfil}
      </TabsContent>
    </Tabs>
  );
}
